import React, { forwardRef, useRef } from "react";

import ScrollToBottomButton from "./ScrollToBottomButton";
import useResizeHandling from "./hooks/useResizeHandling";
import useScrollDetection from "./hooks/useScrollDetection";
import useSidebarStyles from "./hooks/useSidebarStyles";

export interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  hasFooter?: boolean;
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
  className?: string;
  position?: "absolute" | "relative";
  resizable?: boolean;
  onWidthChange?: (width: number) => void;
  contentClassName?: string;
}

const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      isOpen,

      children,
      minWidth = 400,
      maxWidth = 800,
      defaultWidth,
      className = "",
      position = "absolute",
      resizable = true,
      onWidthChange,
      contentClassName = "",
      hasFooter,
    },
    ref,
  ) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Use resize handling hook
    const { sidebarWidth, isCustomWidth, isResizing, startResizing, resizeRef } = useResizeHandling(
      {
        minWidth,
        maxWidth,
        defaultWidth,
        resizable,
        onWidthChange,
        isOpen,
      },
    );

    const { showScrollToBottom, scrollToBottom, handleScroll } = useScrollDetection(
      scrollContainerRef,
      { isOpen, children },
    );

    const { containerClasses, containerStyle } = useSidebarStyles({
      position,
      isOpen,
      sidebarWidth,
      isCustomWidth,
      minWidth,
      maxWidth,
      isResizing,
      className,
    });

    return (
      <div ref={ref} className={containerClasses} style={containerStyle}>
        {resizable && (
          <div
            ref={resizeRef}
            className="group absolute -left-2 top-0 z-20 h-full w-4 cursor-ew-resize select-none bg-transparent"
            onMouseDown={startResizing}
            role="separator"
            aria-orientation="vertical"
          >
            <div className="absolute left-0 right-0 h-full bg-blue-100/0 transition-colors duration-150 group-hover:bg-blue-100/50" />
            <div className="absolute left-1/2 h-full w-px -translate-x-1/2 bg-gray-200 transition-all duration-150 group-hover:bg-blue-600" />
            <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
              <div className="mx-1 h-12 w-[6px] rounded-full bg-blue-500" />
            </div>
          </div>
        )}

        <div
          ref={scrollContainerRef}
          className="relative flex-1 overflow-y-auto"
          onScroll={handleScroll}
        >
          <div className={`h-full p-4 ${contentClassName}`}>{children}</div>
        </div>

        <ScrollToBottomButton
          visible={showScrollToBottom && isOpen}
          onClick={scrollToBottom}
          hasFooter={hasFooter}
        />
      </div>
    );
  },
);

Sidebar.displayName = "Sidebar";

export default Sidebar;

export { default as ScrollToBottomButton } from "./ScrollToBottomButton";
export { default as SidebarWithBlur } from "./SidebarWithBlur";
export type { SidebarWithBlurProps } from "./SidebarWithBlur";
export { default as useBlurDetection } from "./hooks/useBlurDetection";
export { default as useResizeHandling } from "./hooks/useResizeHandling";
export { default as useScrollDetection } from "./hooks/useScrollDetection";
export { default as useSidebarStyles } from "./hooks/useSidebarStyles";
